import { ColDef } from "ag-grid-community";

import { ColumnType } from "components/AgGrid/constants";
import { NameType } from "constants/integrationBusiness";

import {
  formatIntegrationBusinessAddress,
  formatIntegrationBusinessCollection,
  formatIntegrationBusinessContact,
  formatIntegrationBusinessPhone,
} from "lib/agGrid/formatters";

export const IntegrationBusinessColId = {
  NAMES: "4a1700dc-39c4-411b-8f06-ad7cd59448ce",
  ADDRESSES: "6c046f31-b4c2-424b-8a60-203b6a1e2571",
  CONTACTS: "eb3239c3-843d-40ff-a5af-59044ba8d944",
  PHONES: "d7818df0-df29-449e-ba3d-b95e59c7bc8d",
  IS_ACTIVE: "afc6e56c-5b4f-47d4-a7c7-1619454416b5",
};

export const IntegrationBusinessColumnDefinitions: Record<string, ColDef> = {
  [IntegrationBusinessColId.NAMES]: {
    colId: IntegrationBusinessColId.NAMES,
    checkboxSelection: ({ data }) => data && !data.businessId,
    field: "names",
    headerName: "Name",
    sortable: true,
    width: 300,
    filterValueGetter: params => {
      const names = params.getValue(params.colDef.field!) || [];
      return names.map((n: { value: string }) => n.value);
    },
    valueFormatter: params => {
      const names = params.value;
      if (!Array.isArray(names) || names.length === 0) {
        return "";
      }
      return (
        names.find(
          (n: { type: string; value: string }) => n.type === NameType.NAME,
        )?.value || names[0].value
      );
    },
  },

  [IntegrationBusinessColId.ADDRESSES]: {
    colId: IntegrationBusinessColId.ADDRESSES,
    field: "addresses",
    width: 280,
    filterValueGetter: params => {
      const addresses = params.getValue(params.colDef.field!) || [];
      return addresses.map(formatIntegrationBusinessAddress);
    },
    valueFormatter: params => {
      const addresses = params.value;
      if (!Array.isArray(addresses) || addresses.length === 0) {
        return "";
      }
      const completed = formatIntegrationBusinessCollection(
        formatIntegrationBusinessAddress,
        addresses,
      ) as Array<[{ type: string }, string]>;
      if (completed.length === 0) {
        return "";
      }
      if (completed.length === 1) {
        return completed[0][1];
      }
      const first =
        completed.find(([address]) => address.type === "Primary") ||
        completed[0];
      return `${first[1]}; +${completed.length - 1} more...`;
    },
  },

  [IntegrationBusinessColId.CONTACTS]: {
    colId: IntegrationBusinessColId.CONTACTS,
    field: "contacts",
    filterValueGetter: params => {
      const contacts = params.getValue(params.colDef.field!) || [];
      return contacts.map(formatIntegrationBusinessContact);
    },
    valueFormatter: params => {
      const contacts = params.value;
      if (!Array.isArray(contacts) || contacts.length === 0) {
        return "";
      }
      const completed = formatIntegrationBusinessCollection(
        formatIntegrationBusinessContact,
        contacts,
      ) as Array<[{ type: string }, string]>;
      if (completed.length === 0) {
        return "";
      }
      if (completed.length === 1) {
        return completed[0][1];
      }
      const first =
        completed.find(([contact]) => contact.type === "Primary") ||
        completed[0];
      return `${first[1]}; +${completed.length - 1} more...`;
    },
  },

  [IntegrationBusinessColId.PHONES]: {
    colId: IntegrationBusinessColId.PHONES,
    field: "phones",
    width: 160,
    filterValueGetter: params => {
      const phones = params.getValue(params.colDef.field!) || [];
      return phones.map((phone: { phoneName?: string; phoneNumber: string }) =>
        [phone.phoneName, phone.phoneNumber.replace(/[^\d]+/g, "")]
          .filter(Boolean)
          .join(", "),
      );
    },
    valueFormatter: params => {
      const phones = params.value;
      if (!Array.isArray(phones) || phones.length === 0) {
        return "";
      }
      const completed = formatIntegrationBusinessCollection(
        formatIntegrationBusinessPhone,
        phones,
      ) as Array<[{ type: string }, string]>;
      if (completed.length === 0) {
        return "";
      }
      if (completed.length === 1) {
        return completed[0][1];
      }
      const first =
        completed.find(([phone]) => phone.type === "Primary") || completed[0];
      return `${first[1]}; +${completed.length - 1} more...`;
    },
  },

  [IntegrationBusinessColId.IS_ACTIVE]: {
    colId: IntegrationBusinessColId.IS_ACTIVE,
    field: "is_active",
    headerName: "Active",
    width: 160,
    type: ColumnType.BOOLEAN,
    enableRowGroup: true,
  },
};
